/**
 * The compact layout's bottom sheet, as numbers: its three stops, and where a
 * released drag settles (docs/planning/07-mobile-editor.md). Pure, so the feel
 * is tested without a DOM. It knows nothing about the panels in the sheet.
 *
 * Heights are CSS pixels above the bottom of the screen, within `available`:
 * the room below the top bar.
 */

/** Peek shows the tab strip; half keeps the scene live above; full is for reading. */
export type SheetStop = "peek" | "half" | "full";

export const SHEET_STOPS: readonly SheetStop[] = ["peek", "half", "full"];

/** The grabber row and the tab strip. */
export const PEEK_HEIGHT = 72;

/** Room left above a full sheet, so the scene still shows at its top edge. */
const FULL_GAP = 48;

/** A release faster than this (px/ms) is a flick, which moves on a stop. */
export const FLICK_PX_PER_MS = 0.5;

export function sheetHeight(stop: SheetStop, available: number): number {
  switch (stop) {
    case "peek":
      return PEEK_HEIGHT;
    case "half":
      return Math.max(PEEK_HEIGHT, Math.round(available / 2));
    case "full":
      return Math.max(PEEK_HEIGHT, available - FULL_GAP);
  }
}

/** The side panel's width in landscape: a readable column, never most of the screen. */
const SIDE_PANEL = { share: 0.42, min: 320, max: 480 };

/**
 * In landscape the sheet docks to the right as a side panel, like an iPad
 * split view. It's closed at peek and open otherwise: a phone on its side has
 * too little height for a half stop to mean anything.
 */
export function sidePanelWidth(stop: SheetStop, available: number): number {
  if (stop === "peek") return 0;
  const width = Math.round(available * SIDE_PANEL.share);
  return Math.max(SIDE_PANEL.min, Math.min(SIDE_PANEL.max, width));
}

/**
 * Where a drag released at `height` settles. `velocity` is px/ms, positive
 * while the sheet grows. A slow release goes to the nearest stop, a flick to
 * the next stop past the release point in the flick's direction.
 */
export function snapStop(height: number, velocity: number, available: number): SheetStop {
  const stops = SHEET_STOPS.map((stop) => ({ stop, height: sheetHeight(stop, available) }));

  if (Math.abs(velocity) >= FLICK_PX_PER_MS) {
    const ahead =
      velocity > 0
        ? stops.find((candidate) => candidate.height > height)
        : [...stops].reverse().find((candidate) => candidate.height < height);
    const end = velocity > 0 ? stops.at(-1) : stops[0];
    return (ahead ?? end ?? stops[0])?.stop ?? "peek";
  }

  let nearest = stops[0];
  for (const candidate of stops) {
    if (!nearest || Math.abs(candidate.height - height) < Math.abs(nearest.height - height)) {
      nearest = candidate;
    }
  }
  return nearest?.stop ?? "peek";
}
